import { observable, computed } from "mobx";
import { compact, isEmpty } from "lodash";
import debounce from "debounce";
import { FormErrors, FormStore } from "@modules/formx";
import {
  GetCustomerContractProfile,
  GetCustomerContractProfileData,
  ContractsPriceTemplates,
  SubmitPriceListEditorForm,
  ContractsPriceTemplateFragment,
  HighHolidayOptionFragment,
  ContractsPriceRecordFragment,
  CustomerContractCompany
} from "@graphql";
import { store } from "@store";
import { CustomerContractPriceListFormData } from "./CustomerContractPriceListFormData";
import { Navigation } from "@navigation";
import { createObjectID } from "@bonliva-crm/common";
import {
  timeReportService,
  ValidateCustomerContractOptionResult,
  CustomerContractPriceRecordDto
} from "@api/timereports";
import locale from "@bonliva-crm/locale";
import { ContractPriceTemplateRecordInput } from "../../config_editor/ContractsPriceTemplates/ContractsPriceTemplateFormInput";
import { mergeHighHolidays } from "../../config_editor/ContractsPriceTemplates/utils";

export class CustomerContractPriceListFormStore {
  // todo add type formData
  form: FormStore<any>;
  @observable loading = true;
  @observable error: any;
  @observable highHolidaysOptions: HighHolidayOptionFragment[];
  @observable templates: ContractsPriceTemplateFragment[];
  @observable fav: ContractsPriceTemplateFragment[];
  @observable validationResult: ValidateCustomerContractOptionResult[];

  @observable isNew = false;
  @observable professions: string[] = [];
  @observable isBonlivaCare = false;
  @observable isNationalProcurement = false;

  constructor(
    public customerContractId: string,
    public priceListId: string,
    public isDuplicate: boolean,
    public navigation: Navigation
  ) {
    this.isNew = !priceListId;

    this.form = new FormStore({
      classValidatorClassType: CustomerContractPriceListFormData,
      initialValues: new CustomerContractPriceListFormData(),
      onSubmit: () => this.submit(),
      validate: this._validate,
      onSetValue: this._onSetValue
    });
    this.init();
    (window as any).customerContractPriceListFormStore = this;
  }

  init() {
    GetCustomerContractProfileData.func(
      {
        customerContractId: this.customerContractId,
        priceListId: this.priceListId || null,
        isNew: !this.priceListId
      },
      { fetchPolicy: "network-only" }
    )
      .then((res) => {
        if (!this.priceListId) {
          const formValues = CustomerContractPriceListFormData.CreateEmpty(
            res.data.config.defaultTravelZones
          );

          this.form.reset(formValues);
        }

        // if existing price list
        if (res.data.customerContractPriceListById) {
          const resetData = CustomerContractPriceListFormData.Create(
            res.data.customerContractPriceListById,
            res.data.config.defaultTravelZones,
            this.isDuplicate
          );

          resetData.priceGroups = resetData.priceGroups.map((x) => ({
            ...x,
            highHolidays: mergeHighHolidays(res.data.getHighHolidays, x.highHolidays)
          }));

          this.isBonlivaCare =
            res.data.customerContractPriceListById.customerContract.company ===
            CustomerContractCompany.Bonliva_Care;

          // NP-style price columns (2nd-pass article numbers, travel zones) must
          // also show for cloned direct-request contracts, which are non-national
          // (isNationalProcurement=false) but invoice the NP way. Fall back to the
          // legacy flag when useNationalProcurementInvoicing is unset.
          this.isNationalProcurement =
            res.data.customerContractPriceListById.customerContract
              .useNationalProcurementInvoicing ??
            res.data.customerContractPriceListById.customerContract.isNationalProcurement;

          this.professions =
            res.data.customerContractPriceListById.customerContract.professions;

          this.form.reset(resetData);
        }

        // if new pricelist
        if (res.data.customerContractById) {
          this.isBonlivaCare =
            res.data.customerContractById.company ===
            CustomerContractCompany.Bonliva_Care;

          this.isNationalProcurement =
            res.data.customerContractById.useNationalProcurementInvoicing ??
            res.data.customerContractById.isNationalProcurement;

          this.professions = res.data.customerContractById.professions;
        }

        this.highHolidaysOptions = res.data.getHighHolidays;
      })
      .catch((err) => {
        store().snackbars.error(`Cannot load price list data\nReason: ${err}`);
        this.error = err;
      })
      .finally(() => {
        this.loading = false;
      });
    this.getPriceTemplates();
  }

  @computed get customerTreeOptions() {
    return [];
    // return (
    //   this.contract?.orgCustomerTree?.map((x) => ({ ...x, isGroupOpen: !x.ParentId })) ??
    //   []
    // );
  }

  _onSetValue = ({ name, value }) => {
    if (name.includes("records")) {
      const record = this.form.getValue(name.replace(/\.(\w+)$/, ""));
      this.debouncedCheckContractOpt([record]);
    }
  };

  _validate = () => {
    const result: FormErrors<CustomerContractPriceListFormData> = {};
    if (this.form?.values?.priceGroups) {
      this.form.values?.priceGroups.forEach((x, i) => {
        if (x.professions?.length === 0) {
          result[`priceGroups.${i}.professions`] = locale.Required;
        }
      });
    }
    return result;
  };

  validateContractOpt = async (values: ContractsPriceRecordFragment[]) => {
    const items = values.map(mapPriceRecord);

    const result = await timeReportService.validateCustomerContractOptions({
      customerContractPriceRecordDto: items
    });

    const newIds = result.data.map((x) => x.contractOptionId);

    const currentWithoutNew = compact(this.validationResult).filter(
      (x) => !newIds.includes(x.contractOptionId)
    );

    this.validationResult = [...currentWithoutNew, ...result.data];
  };

  getPriceTemplates = (text?: string) => {
    ContractsPriceTemplates.func(
      {
        filter: { text },
        skip: 0,
        limit: 0,
        onlyPrivate: false
      },
      { fetchPolicy: "network-only" }
    ).then((res) => {
      if (res.data.list) {
        this.templates = res.data.list.items.filter((x) => !x.private);
        this.fav = res.data.list.items.filter((x) => x.private);
      }
    });
  };

  submit = () => {
    const data = this.form.values.getPlain();

    data.travelZones = data.travelZones.map((x) => ({
      name: x.name,
      articleNumber: x.articleNumber
    }));

    return SubmitPriceListEditorForm.func(
      {
        customerContractId: this.customerContractId,
        priceListId: this.isDuplicate ? null : this.priceListId,
        data
      },
      { refetchQueries: [GetCustomerContractProfile.OperationName] }
    ).then(({ data }) => {
      if (isEmpty(data.submitCustomerContractPriceListForm.errors)) {
        this.navigation.unBlock("customer_contract_price_list");
        this.navigation.close();
      } else {
        store().snackbars.error(
          `Cannot save form\nReason: ${data.submitCustomerContractPriceListForm.errors.join(
            "\n"
          )}`
        );
      }
    });
  };

  getPriceGroupIndexById(id: string) {
    return this.form.values.priceGroups.findIndex((x) => x._id === id);
  }

  duplicateRecord = (item: ContractPriceTemplateRecordInput, name: string) => {
    const temp = { ...item };
    temp._id = createObjectID();
    this.form.push(`${name}.records`, temp);
    this.form.validate();
  };

  addBlankRecord = (name) => {
    this.form.push(`${name}.records`, ContractPriceTemplateRecordInput.CreateBlank());
    this.form.validate();
  };

  private debouncedCheckContractOpt = debounce(this.validateContractOpt, 400);
}

export function mapPriceRecord(
  v: ContractsPriceRecordFragment
): CustomerContractPriceRecordDto {
  return {
    id: v._id,
    name: "",
    startDay: v.startDay || "",
    endDay: v.endDay || "",
    paidPer: v.paidPer || "",
    currency: v.currency || "",
    workType: v.workType || "",
    from: v.from || "",
    to: v.to || "",
    payPeriod: v.period || "",
    price: 0,
    specialPeriod: null,
    priceList: null,
    startDate: new Date("2021-01-01").toISOString(),
    endDate: new Date("2029-01-01").toISOString()
  };
}
